import React, { useContext, useEffect, useState } from 'react';
import { Heading, Subheading } from '../../components/Heading';
import { Badge } from '../../components/Badge';
import { Select } from '../../components/Select';
import { DateRangePicker } from '../../components/DateRangePicker';
import Loading from '../../components/Loading';
import Pagination from '../../components/Pagination';
import { Card } from '../../components/Card';
import { Tabs, TabsList, TabsTrigger } from '../../components/Tabs';
import { Dialog, DialogActions, DialogBody, DialogDescription, DialogTitle } from '../../components/Dialog';
import { Button } from '../../components/Button';
import { useAuditLog } from '../../hooks/useAuditLog';
import { AuditLog } from '../../models/AuditLog';
import { UserContext } from '../../context/userContext';
import AuditLogService from '../../services/auditLogService';
import showSuccessToast from '../../components/Toasts/ShowSuccessToast';
import showErrorToast from '../../components/Toasts/ShowErrorToast';
import { TrashIcon } from '@heroicons/react/16/solid';
import { ListToolbarRow, FiltersButton, SortMenu } from '../../components/ListToolbar';
import { FiltersDialog, FilterPills, FilterPill } from '../../components/FiltersDialog';
import { Text } from '../../components/Text';

const SORT_OPTIONS = [
    { label: 'Newest first', value: 'newest' },
    { label: 'Oldest first', value: 'oldest' },
];

// Quick-filter shortcuts for the SQL Account sync categories - drive the same `eventType`
// state as the Event Type dropdown below, so either control keeps the other in sync.
const SQL_ACCOUNT_SYNC_TABS = [
    { value: '', label: 'All' },
    { value: 'sql_account.stock_sync', label: 'Inventory Sync' },
    { value: 'sql_account.party_sync', label: 'Customer/Supplier Sync' },
    { value: 'sql_account.do_qr_sync', label: 'DO Sync' },
];

const EVENT_TYPES = [
    { value: '', label: 'All events' },
    { value: 'sql_account.stock_sync', label: 'SQL Account Inventory Sync' },
    { value: 'sql_account.party_sync', label: 'SQL Account Customer/Supplier Sync' },
    { value: 'sql_account.do_qr_sync', label: 'SQL Account DO Sync' },
    { value: 'sql_account.sync', label: 'SQL Account Sync (legacy)' },
    { value: 'user.login', label: 'Web Login' },
    { value: 'app_user.login', label: 'Mobile Login' },
    { value: 'stock_in.create', label: 'Stock In Created' },
    { value: 'stock_in.update', label: 'Stock In Updated' },
    { value: 'stock_in.delete', label: 'Stock In Deleted' },
    { value: 'stock_out.create', label: 'Stock Out Created' },
    { value: 'stock_out.update', label: 'Stock Out Updated' },
    { value: 'stock_out.delete', label: 'Stock Out Deleted' },
    { value: 'stock_adjust.create', label: 'Stock Adjustment Created' },
    { value: 'stock_adjust.update', label: 'Stock Adjustment Updated' },
    { value: 'stock_adjust.delete', label: 'Stock Adjustment Deleted' },
    { value: 'tracking_pickup.create', label: 'Tracking Pickup' },
    { value: 'tracking_delivery.create', label: 'Tracking Delivery' },
    { value: 'tracking_verify.create', label: 'Tracking Verify' },
];

const ACTOR_TYPES = [
    { value: '', label: 'All actors' },
    { value: 'user', label: 'Web User' },
    { value: 'app_user', label: 'Mobile User' },
    { value: 'system', label: 'System' },
];

function formatEventLabel(eventType: string): string {
    const match = EVENT_TYPES.find((e) => e.value === eventType);
    return match ? match.label : eventType;
}

const AuditLogPage: React.FC = () => {
    const { currentUser } = useContext(UserContext);
    const isSuperadmin = currentUser?.rolename === 'Superadmin';
    const [eventType, setEventType] = useState('');
    const [actorType, setActorType] = useState('');
    const [status, setStatus] = useState('');
    const [dateFrom, setDateFrom] = useState('');
    const [dateTo, setDateTo] = useState('');
    const [page, setPage] = useState(1);
    const [selectedLog, setSelectedLog] = useState<AuditLog | null>(null);
    const [purgeDialogOpen, setPurgeDialogOpen] = useState(false);
    const [purging, setPurging] = useState(false);
    const [sortOrder, setSortOrder] = useState('newest');
    const [isFilterOpen, setIsFilterOpen] = useState(false);
    const limit = 20;

    const resetFilters = () => {
        setEventType('');
        setActorType('');
        setStatus('');
        setDateFrom('');
        setDateTo('');
    };

    const activeFilterCount = [eventType, actorType, status, dateFrom, dateTo].filter(Boolean).length;

    const filterPills: FilterPill[] = [
        ...(eventType ? [{ key: 'event', label: `Event: ${formatEventLabel(eventType)}`, onClear: () => setEventType('') }] : []),
        ...(actorType ? [{
            key: 'actor',
            label: `Actor: ${ACTOR_TYPES.find((a) => a.value === actorType)?.label || actorType}`,
            onClear: () => setActorType(''),
        }] : []),
        ...(status ? [{ key: 'status', label: `Status: ${status}`, onClear: () => setStatus('') }] : []),
        ...(dateFrom ? [{ key: 'from', label: `From: ${dateFrom}`, onClear: () => setDateFrom('') }] : []),
        ...(dateTo ? [{ key: 'to', label: `To: ${dateTo}`, onClear: () => setDateTo('') }] : []),
    ];

    const { logs, total, totalPages, loading, error, refetch } = useAuditLog({
        eventType: eventType || undefined,
        actorType: actorType || undefined,
        status: status || undefined,
        dateFrom: dateFrom || undefined,
        dateTo: dateTo ? `${dateTo} 23:59:59` : undefined,
        page,
        limit,
    });

    useEffect(() => {
        document.title = 'Audit Log';
    }, []);

    useEffect(() => {
        setPage(1);
    }, [eventType, actorType, status, dateFrom, dateTo]);

    const handlePurgeOld = async () => {
        setPurging(true);
        try {
            const result = await AuditLogService.deleteOld(30);
            showSuccessToast(`Deleted ${result.deletedCount} entr${result.deletedCount === 1 ? 'y' : 'ies'} older than 30 days.`);
            setPurgeDialogOpen(false);
            setPage(1);
            refetch();
        } catch (err) {
            showErrorToast('Failed to delete old audit log entries.');
        } finally {
            setPurging(false);
        }
    };

    return (
        <>
            <div className="flex flex-wrap items-end justify-between gap-4">
                <div>
                    <Heading>Audit Log</Heading>
                    <p className="mt-1 text-sm text-zinc-500 dark:text-zinc-400">
                        System activity: logins, stock movements, tracking, and SQL Account sync runs.
                    </p>
                </div>
                {isSuperadmin && (
                    <Button outline onClick={() => setPurgeDialogOpen(true)}>
                        <TrashIcon />
                        Delete records over 30 days
                    </Button>
                )}
            </div>

            <Card className="mt-6 p-5">
            <ListToolbarRow>
                <FiltersButton count={activeFilterCount} onClick={() => setIsFilterOpen(true)} />
                <SortMenu options={SORT_OPTIONS} value={sortOrder} onChange={setSortOrder} />
            </ListToolbarRow>
            <FilterPills pills={filterPills} onClearAll={resetFilters} />
            <div>
                <Tabs value={eventType} onValueChange={setEventType}>
                    <TabsList>
                        {SQL_ACCOUNT_SYNC_TABS.map((tab) => (
                            <TabsTrigger key={tab.value} value={tab.value}>{tab.label}</TabsTrigger>
                        ))}
                    </TabsList>
                </Tabs>
            </div>

            <div className="mt-6" />

            {loading && <div className="w-full text-center h-full py-36 justify-center"><Loading /></div>}
            {error && <p className="py-16 text-center text-red-700">{error}</p>}
            {!loading && !error && logs.length === 0 && <p className="py-16 text-center">No audit log entries found.</p>}

            {!loading && !error && logs.length > 0 && (
                <ul className="space-y-3">
                    {[...logs].sort((a, b) => {
                        const diff = new Date(a.created_time).getTime() - new Date(b.created_time).getTime();
                        return sortOrder === 'newest' ? -diff : diff;
                    }).map((log) => (
                        <li
                            key={log.id}
                            onClick={() => setSelectedLog(log)}
                            role="button"
                            tabIndex={0}
                            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') setSelectedLog(log); }}
                            className="cursor-pointer rounded-xl px-4 py-3 sm:px-5 sm:py-4 hover:bg-secondary transition-colors"
                        >
                            <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
                                <div className="min-w-0 flex-1">
                                    <div className="flex flex-wrap items-center gap-2">
                                        <Badge color="zinc">{formatEventLabel(log.event_type)}</Badge>
                                        <Badge color={log.status === 'success' ? 'green' : 'red'}>
                                            {log.status.toUpperCase()}
                                        </Badge>
                                        {log.actor_name && (
                                            <span className="break-words text-sm font-medium text-zinc-900 dark:text-white">
                                                {log.actor_name}
                                            </span>
                                        )}
                                    </div>
                                    {log.description && (
                                        <p className="mt-1 break-words text-sm text-zinc-600 dark:text-zinc-400">{log.description}</p>
                                    )}
                                    {log.ip_address && (
                                        <p className="mt-1 text-xs text-zinc-400 dark:text-zinc-500">IP: {log.ip_address}</p>
                                    )}
                                </div>
                                <p
                                    className="shrink-0 text-xs text-zinc-400 dark:text-zinc-500 sm:whitespace-nowrap sm:text-right"
                                    title={new Date(log.created_time).toLocaleString()}
                                >
                                    {new Date(log.created_time).toLocaleString()}
                                </p>
                            </div>
                        </li>
                    ))}
                </ul>
            )}

            {!loading && !error && total > 0 && (
                <Pagination
                    currentPage={page}
                    totalPages={totalPages}
                    onPageChange={setPage}
                    totalResult={total}
                    dataName="entries"
                    pageSize={limit}
                />
            )}
            </Card>

            <FiltersDialog open={isFilterOpen} onClose={() => setIsFilterOpen(false)} onReset={resetFilters}>
                <div>
                    <Text>Event Type</Text>
                    <Select value={eventType} onChange={(e) => setEventType(e.target.value)} aria-label="Event type">
                        {EVENT_TYPES.map((opt) => (
                            <option key={opt.value} value={opt.value}>{opt.label}</option>
                        ))}
                    </Select>
                </div>
                <div>
                    <Text>Actor Type</Text>
                    <Select value={actorType} onChange={(e) => setActorType(e.target.value)} aria-label="Actor type">
                        {ACTOR_TYPES.map((opt) => (
                            <option key={opt.value} value={opt.value}>{opt.label}</option>
                        ))}
                    </Select>
                </div>
                <div>
                    <Text>Status</Text>
                    <Select value={status} onChange={(e) => setStatus(e.target.value)} aria-label="Status">
                        <option value="">All statuses</option>
                        <option value="success">Success</option>
                        <option value="failure">Failure</option>
                    </Select>
                </div>
                <div>
                    <Text>Date Range</Text>
                    <DateRangePicker
                        from={dateFrom}
                        to={dateTo}
                        onChange={({ from, to }) => { setDateFrom(from); setDateTo(to); }}
                        placeholder="All dates"
                        className="w-full"
                    />
                </div>
            </FiltersDialog>

            <Dialog size="2xl" open={!!selectedLog} onClose={() => setSelectedLog(null)}>
                {selectedLog && (
                    <>
                        <DialogTitle>
                            <div className="flex flex-wrap items-center gap-2">
                                <Badge color="zinc">{formatEventLabel(selectedLog.event_type)}</Badge>
                                <Badge color={selectedLog.status === 'success' ? 'green' : 'red'}>
                                    {selectedLog.status.toUpperCase()}
                                </Badge>
                            </div>
                        </DialogTitle>

                        <div className="mt-4 space-y-4">
                            <div>
                                <Subheading>Actor</Subheading>
                                <p className="mt-1 text-sm text-zinc-700 dark:text-zinc-300">
                                    {selectedLog.actor_name || '-'}
                                    {selectedLog.actor_type ? ` (${selectedLog.actor_type})` : ''}
                                </p>
                            </div>
                            <div>
                                <Subheading>Time</Subheading>
                                <p className="mt-1 text-sm text-zinc-700 dark:text-zinc-300">
                                    {new Date(selectedLog.created_time).toLocaleString()}
                                </p>
                            </div>
                            {selectedLog.description && (
                                <div>
                                    <Subheading>Description</Subheading>
                                    <p className="mt-1 whitespace-pre-wrap break-words text-sm text-zinc-700 dark:text-zinc-300">
                                        {selectedLog.description}
                                    </p>
                                </div>
                            )}
                            {selectedLog.ip_address && (
                                <div>
                                    <Subheading>IP Address</Subheading>
                                    <p className="mt-1 text-sm text-zinc-700 dark:text-zinc-300">{selectedLog.ip_address}</p>
                                </div>
                            )}
                            {selectedLog.entity_id != null && (
                                <div>
                                    <Subheading>Entity ID</Subheading>
                                    <p className="mt-1 text-sm text-zinc-700 dark:text-zinc-300">{selectedLog.entity_id}</p>
                                </div>
                            )}
                            {selectedLog.metadata && (
                                <div>
                                    <Subheading>Details</Subheading>
                                    <pre className="mt-1 max-h-96 overflow-auto whitespace-pre-wrap break-words rounded-md bg-zinc-950/[0.03] p-3 text-xs text-zinc-700 dark:bg-white/[0.03] dark:text-zinc-300">
                                        {JSON.stringify(selectedLog.metadata, null, 2)}
                                    </pre>
                                </div>
                            )}
                        </div>

                        <DialogActions>
                            <Button plain onClick={() => setSelectedLog(null)}>Close</Button>
                        </DialogActions>
                    </>
                )}
            </Dialog>

            <Dialog size="md" open={purgeDialogOpen} onClose={() => !purging && setPurgeDialogOpen(false)}>
                <DialogTitle>Delete records over 30 days?</DialogTitle>
                <DialogDescription>
                    This permanently deletes every audit log entry older than 30 days. This cannot be undone.
                </DialogDescription>
                <DialogBody />
                <DialogActions>
                    <Button plain onClick={() => setPurgeDialogOpen(false)} disabled={purging}>Cancel</Button>
                    <Button color="red" onClick={handlePurgeOld} disabled={purging}>
                        {purging ? 'Deleting...' : 'Delete'}
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
};

export default AuditLogPage;
